"use client"

import { useCallback, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { asArray } from "@/lib/as-array"
import { getCommentLabel, type CommentLabel } from "@/lib/comment-labels"
import {
  useDeleteCommentsCommentId,
  useGetComments,
  usePostComments,
  usePutCommentsCommentId,
} from "openapi/api/endpoints"
import type {
  Comment,
  PostCommentsBody,
} from "openapi/api/model"

export type CommentScope = "job" | "all"

/**
 * One comment, as the view screen needs it.
 *
 * Canvas 14a reads provenance as a spec sheet: author, job, and "the stage the
 * comment was written at, which is the fact the old UI never recorded". All
 * three are real, but they come from two places. `GET /comments` carries the
 * author and nothing else, while `GET /jobs/{id}/pipeline/comments` records the
 * stage each comment was written at — so the stage is joined on by id, and only
 * when the record was opened inside a job. The job's own title is context the
 * caller already holds.
 *
 * The artboard writes the stage as "screening · day 11". The day count is not
 * stored anywhere readable, so the stage goes in without it.
 */
export type CommentDetail = {
  id: string
  label: string
  labelValue: CommentLabel
  /** Rich text, stored as HTML. */
  body: string
  /** "28 Jul 2026, 16:02" — a machine value, so it renders mono. */
  writtenAt?: string
  author?: string
  /** The job this comment was written against, when there is one. */
  job?: string
  /** Robin's own read of the comment, when he left one. */
  agentNote?: string
}

/** One row in the related-comments rail. */
export type CommentSummary = {
  id: string
  label: string
  preview: string
  author?: string
  /** "28 Jul" — short and mono. */
  when?: string
  /** Epoch ms, only for ordering. Undated sorts oldest. */
  writtenAtValue: number
}

/** Epoch ms for ordering; an unparseable date sorts oldest rather than nowhere. */
function writtenAtValue(iso?: string) {
  if (!iso) return -Infinity
  const time = new Date(iso).getTime()
  return Number.isNaN(time) ? -Infinity : time
}

function formatWrittenAt(iso?: string) {
  if (!iso) return undefined
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
}

function formatShortDate(iso?: string) {
  if (!iso) return undefined
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" })
}

/** Bodies are HTML; the rail shows a couple of lines of prose from them. */
function plainText(html: string) {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim()
}

/**
 * `agent_provenance` is typed as an open bag, so read it defensively and only
 * surface a note when one is genuinely in there.
 */
function readAgentNote(comment: Comment) {
  const bag = comment.agent_provenance as Record<string, unknown> | undefined
  if (!bag) return undefined
  for (const key of ["summary", "note", "conclusion", "message", "reasoning"]) {
    const value = bag[key]
    if (typeof value === "string" && value.trim()) return value.trim()
  }
  return undefined
}

type Args = {
  candidateId: string
  /** Present when the record was opened from inside a job. */
  jobId?: string | null
  /** The comment currently open, if any. */
  selectedId?: string | null
}

/**
 * Every comment concern for the candidate record: the list, the one on screen,
 * the scope toggle and the three mutations.
 *
 * The view components take what this returns and render it — they do no
 * fetching, deriving or toasting of their own. Both entry points, the
 * candidates page and a job's pipeline, go through here, so a comment behaves
 * identically whichever way the record was opened.
 */
export function useCandidateComments({
  candidateId,
  jobId,
  selectedId,
}: Args) {
  const canScopeToJob = Boolean(jobId)
  const [scope, setScope] = useState<CommentScope>(canScopeToJob ? "job" : "all")

  const params = useMemo(
    () => ({
      entity_id: candidateId,
      entity_type: "candidate" as const,
      ...(canScopeToJob && scope === "job" ? { job_id: jobId as string } : {}),
    }),
    [candidateId, canScopeToJob, jobId, scope],
  )

  const { data, isFetching, isPending, isError } = useGetComments(params, {
    query: { enabled: Boolean(candidateId) },
  })

  const comments = useMemo(() => asArray<Comment>(data), [data])

  /*
    The scope filters the rail; it does not decide whether the open comment
    exists. The open comment is found in the rail's list when it is there —
    instantly, so a row can grow into the comment it opens — and otherwise
    among all of the candidate's comments. A comment opened by link need not be
    on this job (Robin writes candidate notes with no job), and the scoped list
    alone called it "no longer available", including after switching the rail
    back to the job's comments. The wider read is made only when the scoped
    list has loaded without it; with the scope on "all" it is the same request.
  */
  const inScope = comments.some((comment) => comment.id === selectedId)
  const allParams = useMemo(
    () => ({ entity_id: candidateId, entity_type: "candidate" as const }),
    [candidateId],
  )
  const {
    data: everyComment,
    isFetching: isFetchingAll,
    isError: isAllError,
    refetch: refetchAll,
  } = useGetComments(allParams, {
    query: { enabled: Boolean(candidateId && selectedId && !isPending && !inScope) },
  })
  const isOpening = Boolean(selectedId) && !inScope && (isPending || isFetchingAll)
  /*
    A failed read is not a missing comment: without this the overlay said "no
    longer available" about a comment it simply could not fetch.
  */
  const openFailed = Boolean(selectedId) && !inScope && !isFetchingAll && isAllError

  const queryClient = useQueryClient()
  const { mutateAsync: postComment, isPending: isPosting } = usePostComments()
  const { mutateAsync: putComment, isPending: isSaving } = usePutCommentsCommentId()
  const { mutateAsync: removeComment, isPending: isDeleting } =
    useDeleteCommentsCommentId()

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["/comments"], exact: false })
  }, [queryClient])

  const selected = useMemo<CommentDetail | undefined>(() => {
    const found =
      comments.find((comment) => comment.id === selectedId) ??
      asArray<Comment>(everyComment).find((comment) => comment.id === selectedId)
    if (!found) return undefined
    return {
      id: found.id as string,
      label: getCommentLabel(found.label ?? ""),
      labelValue: (found.label ?? "note") as CommentLabel,
      body: found.body ?? "",
      writtenAt: formatWrittenAt(found.created_at),
      author: found.username ?? undefined,
      // The job the comment was written on, from the comment itself. This
      // used to be the record's job, stamped on every comment — so a
      // candidate-level note opened from a job read as written on that job.
      job: found.job_title ?? undefined,
      agentNote: readAgentNote(found),
    }
  }, [comments, everyComment, selectedId])

  const [search, setSearch] = useState("")

  /*
    Newest first. Nothing ordered these before, so the record's rail — which
    shows five of them — was showing whichever five the endpoint happened to
    return first. On a record with sixty comments that reads as "the latest
    five" while being nothing of the sort.
  */
  const all = useMemo<CommentSummary[]>(
    () =>
      comments
        .filter((comment) => comment.id)
        .map((comment) => ({
          id: comment.id as string,
          label: getCommentLabel(comment.label ?? ""),
          preview: plainText(comment.body ?? "") || "(empty comment)",
          author: comment.username ?? undefined,
          when: formatShortDate(comment.created_at),
          writtenAtValue: writtenAtValue(comment.created_at),
        }))
        .sort((a, b) => b.writtenAtValue - a.writtenAtValue),
    [comments],
  )

  /*
    Searching the rail, over the same three fields the pre-Graphite viewer
    searched: what the comment says, what kind it is, and who wrote it. The
    artboards do not draw a search box on this screen, which is the artboard
    being a picture — the shipped one had it and people use it.
  */
  const related = useMemo<CommentSummary[]>(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return all
    return all.filter(
      (comment) =>
        comment.preview.toLowerCase().includes(needle) ||
        comment.label.toLowerCase().includes(needle) ||
        (comment.author ?? "").toLowerCase().includes(needle),
    )
  }, [all, search])

  const create = useCallback(
    async (label: CommentLabel, body: string) => {
      const payload: PostCommentsBody = jobId
        ? {
            label,
            body,
            entity_type: "job_candidate",
            job_id: jobId,
            candidate_id: candidateId,
          }
        : { label, body, entity_type: "candidate", entity_id: candidateId }

      try {
        await postComment({ data: payload })
        await refresh()
        toast.success("Comment posted")
        return true
      } catch (error) {
        toast.error("Could not post the comment", { description: `${error}` })
        return false
      }
    },
    [candidateId, jobId, postComment, refresh],
  )

  const update = useCallback(
    async (commentId: string, label: CommentLabel, body: string) => {
      try {
        await putComment({ commentId, data: { label, body } as never })
        await refresh()
        toast.success("Comment updated")
        return true
      } catch (error) {
        toast.error("Could not save the comment", { description: `${error}` })
        return false
      }
    },
    [putComment, refresh],
  )

  const destroy = useCallback(
    async (commentId: string) => {
      try {
        await removeComment({ commentId })
        await refresh()
        toast.success("Comment deleted")
        return true
      } catch (error) {
        toast.error("Could not delete the comment", { description: `${error}` })
        return false
      }
    },
    [refresh, removeComment],
  )

  return {
    count: comments.length,
    related,
    /** Rows before the search narrowed them, so a count can say both. */
    relatedTotal: all.length,
    search,
    setSearch,
    selected,
    isFetching,
    /** Whether the open comment is still being read. */
    isOpening,
    /** The open comment could not be read (as opposed to: read, and not there). */
    openFailed,
    retryOpen: () => void refetchAll(),
    /*
      Whether the list is known at all. `comments` falls back to empty, so the
      record showed "Comments 0" and "No comments yet" on a candidate with a
      dozen of them while this was still in flight, and permanently if it failed.
    */
    isCounted: !isPending && !isError,
    /*
      Carried separately, because `isCounted` is false for two very different
      reasons and a list that only sees the combined flag says "Reading the
      comments…" forever after a failure.
    */
    isError,
    scope,
    setScope,
    canScopeToJob,
    create,
    update,
    destroy,
    isBusy: isPosting || isSaving || isDeleting,
  }
}
